import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Plus, Crown, User, Trash2, KeyRound, Edit3, Building2, CalendarClock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { operacionesApi } from '../../lib/api';
import { formatDate } from '../../lib/utils';

const empty = { nombre: '', apellido: '', dni: '', fecha_nacimiento: '', email: '', telefono: '', activo: true };

export default function EdificioMiUnidad() {
  const [data, setData] = useState(null);
  const [misReservas, setMisReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [r, mr] = await Promise.all([
        operacionesApi.miUnidadResumen(),
        operacionesApi.misReservas(),
      ]);
      setData(r.data);
      setMisReservas(mr.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !data) return <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>;

  const yo = data.yo;
  const esMaster = yo?.es_master;
  const miembros = data.miembros || [];
  const master = miembros.find(m => m.es_master);
  const autorizados = miembros.filter(m => !m.es_master);

  const openNew = () => { setForm(empty); setDialog({ mode: 'new' }); };
  const openEdit = (m) => {
    setForm({
      nombre: m.nombre, apellido: m.apellido, dni: m.dni,
      fecha_nacimiento: m.fecha_nacimiento || '', email: m.email,
      telefono: m.telefono || '', activo: m.activo,
    });
    setDialog({ mode: 'edit', target: m });
  };
  const close = () => setDialog(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let res;
      if (dialog.mode === 'new') {
        res = await operacionesApi.crearAutorizado(yo.unidad_id, form);
      } else {
        res = await operacionesApi.actualizarUsuario(dialog.target.usuario_edif_id, form);
      }
      const pw = res?.data?.password_temporal;
      if (pw) toast.success(`Autorizado creado. Contraseña: ${pw}`, { duration: 30000 });
      else toast.success('Guardado');
      close();
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const del = async (m) => {
    if (!confirm(`¿Eliminar a ${m.nombre} ${m.apellido}? Sus reservas futuras se cancelan.`)) return;
    try {
      await operacionesApi.eliminarUsuario(m.usuario_edif_id);
      toast.success('Eliminado');
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const resetPw = async (m) => {
    if (!confirm(`Resetear contraseña de ${m.email}?`)) return;
    try {
      const r = await operacionesApi.resetPassword(m.usuario_edif_id);
      toast.success(`Nueva contraseña: ${r.data.password_temporal}`, { duration: 30000 });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-5 h-5 text-amber-700" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">{data.unidad?.nombre_unidad || 'Mi unidad'}</h1>
          <p className="text-xs text-slate-500">{data.edificio?.nombre || ''}</p>
        </div>
      </div>

      {/* Mis próximas reservas */}
      <section className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="flex items-center gap-2 mb-2">
          <CalendarClock className="w-4 h-4 text-slate-500" />
          <h3 className="font-semibold text-slate-900 text-sm">Mis próximas reservas</h3>
        </div>
        {misReservas.length === 0 ? (
          <p className="text-xs text-slate-400 py-2">No tenés reservas próximas.</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {misReservas.slice(0, 8).map(r => (
              <li key={r.reserva_id} className="py-1.5 flex items-center justify-between">
                <div>
                  <span className="font-medium">{r.sala_nombre}</span>
                  <span className="text-slate-500 mx-2">·</span>
                  <span className="text-slate-700">{formatDate(r.fecha)} · {r.hora_inicio}–{r.hora_fin}</span>
                  {r.notas && <span className="text-slate-400 italic ml-2">"{r.notas}"</span>}
                </div>
                <button
                  onClick={async () => {
                    if (!confirm(`Cancelar reserva ${formatDate(r.fecha)} ${r.hora_inicio}?`)) return;
                    try {
                      await operacionesApi.cancelarReserva(r.reserva_id);
                      toast.success('Cancelada');
                      reload();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
                  }}
                  className="text-xs text-red-500 hover:underline">Cancelar</button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Mi unidad: master + autorizados */}
      <section className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-900 text-sm">Personas con acceso</h3>
          {esMaster && (
            <Button size="sm" onClick={openNew} data-testid="btn-add-autorizado">
              <Plus className="w-3 h-3 mr-1" /> Agregar autorizado
            </Button>
          )}
        </div>
        <div className="divide-y divide-slate-100">
          {master && (
            <Persona m={master} esMio={master.usuario_edif_id === yo.usuario_edif_id} />
          )}
          {autorizados.map(m => (
            <Persona
              key={m.usuario_edif_id}
              m={m}
              esMio={m.usuario_edif_id === yo.usuario_edif_id}
              canEdit={esMaster}
              onEdit={() => openEdit(m)}
              onDelete={() => del(m)}
              onResetPw={() => resetPw(m)}
            />
          ))}
          {!autorizados.length && (
            <div className="text-xs text-slate-400 py-2">{esMaster ? 'Aún no agregaste autorizados.' : 'Tu master no agregó otros autorizados.'}</div>
          )}
        </div>
      </section>

      {/* Dialog */}
      <Dialog open={!!dialog} onOpenChange={o => !o && close()}>
        <DialogContent className="max-w-lg">
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>{dialog?.mode === 'new' ? 'Nuevo autorizado' : `Editar ${dialog?.target?.nombre}`}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-2">
              <div><Label>Nombre *</Label><Input required value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} /></div>
              <div><Label>Apellido *</Label><Input required value={form.apellido} onChange={e => setForm({ ...form, apellido: e.target.value })} /></div>
              <div><Label>DNI *</Label><Input required value={form.dni} onChange={e => setForm({ ...form, dni: e.target.value })} /></div>
              <div><Label>Fecha nac.</Label><Input type="date" value={form.fecha_nacimiento || ''} onChange={e => setForm({ ...form, fecha_nacimiento: e.target.value })} /></div>
              <div className="col-span-2"><Label>Email *</Label><Input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div className="col-span-2"><Label>Teléfono</Label><Input value={form.telefono || ''} onChange={e => setForm({ ...form, telefono: e.target.value })} /></div>
              {dialog?.mode === 'edit' && (
                <div className="col-span-2 flex items-center gap-2">
                  <input type="checkbox" id="act" checked={form.activo} onChange={e => setForm({ ...form, activo: e.target.checked })} />
                  <Label htmlFor="act">Activo</Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Persona = ({ m, esMio, canEdit, onEdit, onDelete, onResetPw }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-center gap-2 text-sm">
      {m.es_master ? <Crown className="w-3.5 h-3.5 text-amber-600" /> : <User className="w-3.5 h-3.5 text-slate-400" />}
      <span className="font-medium text-slate-900">{m.nombre} {m.apellido}</span>
      {esMio && <span className="text-[10px] uppercase bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">vos</span>}
      <span className="text-slate-400 text-xs">DNI {m.dni}</span>
      <span className="text-slate-500 text-xs">· {m.email}</span>
      {!m.activo && <span className="text-red-500 text-xs">(inactivo)</span>}
    </div>
    {canEdit && !m.es_master && (
      <div className="flex items-center gap-1">
        <button onClick={onEdit} className="p-1 hover:bg-slate-100 rounded"><Edit3 className="w-3.5 h-3.5 text-slate-500" /></button>
        <button onClick={onResetPw} className="p-1 hover:bg-slate-100 rounded"><KeyRound className="w-3.5 h-3.5 text-slate-500" /></button>
        <button onClick={onDelete} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
      </div>
    )}
  </div>
);
